import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Check, X, Loader2, Info } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const FEATURE_LIST = [
  { 
    key: 'basic_profile', 
    label: 'Basic profile',
    description: 'Create your business profile with essential information'
  },
  { 
    key: 'directory_listing', 
    label: 'Directory listing',
    description: 'Get listed in our comprehensive sauna directory'
  },
  { 
    key: 'single_image_upload', 
    label: 'Single image only',
    description: 'Upload one main image for your listing'
  },
  { 
    key: 'service_listings', 
    label: 'Service listings',
    description: 'List your sauna services and treatments'
  },
  { 
    key: 'booking_link', 
    label: 'Booking link',
    description: 'Add a link to your booking system'
  },
  { 
    key: 'gallery_images', 
    label: 'Multiple image uploads',
    description: 'Upload 10+ images to showcase your sauna'
  },
  { 
    key: 'phone_number', 
    label: 'Always show contact button',
    description: 'Contact button always visible to potential guests'
  },
  { 
    key: 'analytics', 
    label: 'Full analytics with visitor insights',
    description: 'Track visitor behavior and get detailed insights'
  },
  { 
    key: 'featured_card', 
    label: 'Top placement with visual card & image',
    description: 'Featured placement with enhanced visual presentation'
  },
];

const featureMap: Record<string, { free: boolean | string; premium: boolean | string }> = {
  basic_profile: { free: true, premium: true },
  directory_listing: { free: true, premium: true },
  single_image_upload: { free: true, premium: false },
  service_listings: { free: 'Up to 2', premium: 'Unlimited' },
  booking_link: { free: true, premium: true },
  gallery_images: { free: false, premium: '10+' },
  phone_number: { free: false, premium: true },
  analytics: { free: false, premium: true },
  featured_card: { free: false, premium: true },
};

export function PricingPlans() {
  const { status, upgrade, isUpgrading } = useSubscription();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleSubscription = (planId: string) => {
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: "/subscription" } });
      return;
    }
    upgrade(planId);
  };

  if (!status) {
    return (
      <div className="flex flex-col items-center justify-center h-48 w-full">
        <Loader2 className="h-10 w-10 animate-spin text-sauna-orange mb-4" />
        <p className="text-gray-600">Loading subscription plans...</p>
      </div>
    );
  }

  // Only show Free and Premium
  const free = status.available_plans.find((p: any) => p.id === 'free');
  const premium = status.available_plans.find((p: any) => p.id === 'premium');
  const isLoggedIn = isAuthenticated();

  return (
    <TooltipProvider>
      <div className="w-full flex flex-col items-center justify-center">
        {/* Headline Subcopy */}
        <div className="text-center mb-8">
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stand out. Add images. Show temps. Attract more guests.
          </p>
        </div>
        
        <div className="w-full max-w-7xl mx-auto">
          {/* Desktop Layout with Feature Labels */}
          <div className="hidden lg:grid grid-cols-3 gap-8 items-start">
            {/* Feature Labels Column */}
            <div className="flex flex-col">
              <div className="h-52" /> {/* Fine-tuned spacer for perfect alignment */}
              {FEATURE_LIST.map((feature) => (
                <div key={feature.key} className="py-4 pr-4 border-b border-gray-100 text-base font-medium text-gray-700 h-16 flex items-center justify-between">
                  <span className="flex-1">{feature.label}</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-gray-400 hover:text-sauna-orange flex-shrink-0 ml-2" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">{feature.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}
            </div>
            
            {/* Free Plan Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow flex flex-col items-center px-6 py-6 min-w-[280px] max-w-xs mx-auto relative">
              <div className="text-2xl font-bold text-sauna-navy mb-1 text-center">Free</div>
              <div className="text-4xl font-extrabold text-sauna-navy mb-1 text-center">€0<span className="text-base font-normal">/mo</span></div>
              <div className="text-xs text-gray-400 mb-3 text-center">Limited features</div>
              <Button
                onClick={() => {
                  if (!isLoggedIn) {
                    navigate('/signup/operator');
                    return;
                  }
                  handleSubscription('free');
                }}
                disabled={free?.is_current || isUpgrading}
                className="w-full border border-sauna-orange text-sauna-orange bg-white hover:bg-sauna-orange/10 font-semibold text-lg py-2 mb-3 rounded-xl shadow-sm"
              >
                {!isLoggedIn ? 'Get Free' : (free?.is_current ? 'Current Plan' : 'Continue limited')}
              </Button>
              <div className="text-xs text-gray-400 mb-3 text-center">Free plan</div>
              <div className="flex flex-col w-full">
                {FEATURE_LIST.map((feature) => {
                  const featureValue = featureMap[feature.key];
                  return (
                    <div key={feature.key} className="h-16 flex items-center justify-center border-b border-gray-100">
                      {typeof featureValue?.free === 'boolean'
                        ? featureValue.free
                          ? <Check className="h-5 w-5 text-green-500" />
                          : <X className="h-5 w-5 text-red-400" />
                        : <span className="font-medium text-gray-700 text-sm">{featureValue?.free || ''}</span>
                      }
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Premium Plan Card */}
            <div className="bg-white rounded-2xl border-2 border-sauna-orange shadow-lg flex flex-col items-center px-6 py-6 min-w-[280px] max-w-xs mx-auto relative">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                <span className="bg-sauna-blue text-white text-xs font-bold px-6 py-2 rounded-full shadow-lg tracking-wide">RECOMMENDED</span>
              </div>
              <div className="text-2xl font-bold text-sauna-navy mb-1 text-center">Premium</div>
              <div className="text-4xl font-extrabold text-sauna-orange mb-1 text-center">€{premium?.price || '49'}<span className="text-base font-normal text-sauna-navy">/mo</span></div>
              {premium?.trial_days > 0 ? (
                <div className="mb-1 text-sauna-orange text-sm font-semibold text-center">{premium.trial_days}-day free trial</div>
              ) : (
                <div className="mb-1 text-transparent text-sm font-semibold text-center">-</div>
              )}
              <Button
                onClick={() => {
                  if (!isLoggedIn) {
                    navigate('/signup/operator');
                    return;
                  }
                  handleSubscription('premium');
                }}
                disabled={premium?.is_current || isUpgrading}
                className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white font-semibold text-lg py-2 mb-3 rounded-xl shadow-md"
              >
                {premium?.is_current ? 'Current Plan' : 'Start Free Trial'}
              </Button>
              <div className="text-xs text-sauna-navy mb-3 text-center">100% money-back guarantee</div>
              <div className="flex flex-col w-full">
                {FEATURE_LIST.map((feature) => {
                  const featureValue = featureMap[feature.key];
                  return (
                    <div key={feature.key} className="h-16 flex items-center justify-center border-b border-gray-100">
                      {typeof featureValue?.premium === 'boolean'
                        ? featureValue.premium
                          ? <Check className="h-5 w-5 text-green-500" />
                          : <X className="h-5 w-5 text-red-400" />
                        : <span className="font-medium text-gray-700 text-sm">{featureValue?.premium || ''}</span>
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile Layout without Feature Labels */}
          <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Free Plan Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow flex flex-col items-center px-6 py-8 relative">
              <div className="text-2xl font-bold text-sauna-navy mb-1 text-center">Free</div>
              <div className="text-4xl font-extrabold text-sauna-navy mb-1 text-center">€0<span className="text-base font-normal">/mo</span></div>
              <div className="text-xs text-gray-400 mb-4 text-center">Limited features</div>
              <Button
                onClick={() => {
                  if (!isLoggedIn) {
                    navigate('/signup/operator');
                    return;
                  }
                  handleSubscription('free');
                }}
                disabled={free?.is_current || isUpgrading}
                className="w-full border border-sauna-orange text-sauna-orange bg-white hover:bg-sauna-orange/10 font-semibold text-lg py-2 mb-4 rounded-xl shadow-sm"
              >
                {!isLoggedIn ? 'Get Free' : (free?.is_current ? 'Current Plan' : 'Continue limited')}
              </Button>
              
              {/* Mobile Feature List */}
              <div className="w-full space-y-3">
                {FEATURE_LIST.map((feature) => {
                  const featureValue = featureMap[feature.key];
                  return (
                    <div key={feature.key} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">{feature.label}</span>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400 hover:text-sauna-orange" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{feature.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="flex-shrink-0">
                        {typeof featureValue?.free === 'boolean'
                          ? featureValue.free
                            ? <Check className="h-4 w-4 text-green-500" />
                            : <X className="h-4 w-4 text-red-400" />
                          : <span className="font-medium text-gray-700 text-sm">{featureValue?.free || ''}</span>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Premium Plan Card */}
            <div className="bg-white rounded-2xl border-2 border-sauna-orange shadow-lg flex flex-col items-center px-6 py-8 relative">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                <span className="bg-sauna-blue text-white text-xs font-bold px-6 py-2 rounded-full shadow-lg tracking-wide">RECOMMENDED</span>
              </div>
              <div className="text-2xl font-bold text-sauna-navy mb-1 text-center">Premium</div>
              <div className="text-4xl font-extrabold text-sauna-orange mb-1 text-center">€{premium?.price || '49'}<span className="text-base font-normal text-sauna-navy">/mo</span></div>
              {premium?.trial_days > 0 ? (
                <div className="mb-1 text-sauna-orange text-sm font-semibold text-center">{premium.trial_days}-day free trial</div>
              ) : (
                <div className="mb-1 text-transparent text-sm font-semibold text-center">-</div>
              )}
              <Button
                onClick={() => {
                  if (!isLoggedIn) {
                    navigate('/signup/operator');
                    return;
                  }
                  handleSubscription('premium');
                }}
                disabled={premium?.is_current || isUpgrading}
                className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white font-semibold text-lg py-2 mb-2 rounded-xl shadow-md"
              >
                {premium?.is_current ? 'Current Plan' : 'Start Free Trial'}
              </Button>
              <div className="text-xs text-sauna-navy mb-4 text-center">100% money-back guarantee</div>
              
              {/* Mobile Feature List */}
              <div className="w-full space-y-3">
                {FEATURE_LIST.map((feature) => {
                  const featureValue = featureMap[feature.key];
                  return (
                    <div key={feature.key} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">{feature.label}</span>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400 hover:text-sauna-orange" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{feature.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="flex-shrink-0">
                        {typeof featureValue?.premium === 'boolean'
                          ? featureValue.premium
                            ? <Check className="h-4 w-4 text-green-500" />
                            : <X className="h-4 w-4 text-red-400" />
                          : <span className="font-medium text-gray-700 text-sm">{featureValue?.premium || ''}</span>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
